import React, { useState } from 'react';
import { Form, Input, Button, notification, Card } from 'antd';
import { motion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import apiClient from '@/utils/apiClient';
import { useNavigate } from 'react-router-dom';
import momentTimeZone from 'moment-timezone';
import './ReceptionistDetailsStep.scss';
import { Features } from '@/types/TReseller';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { updateOnboardingInfo } from '@/store/resellerOnboardingSteps/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import posthog from 'posthog-js';
import { createUser } from '@/api/user';
import { useMutation } from '@tanstack/react-query';
import { getLoggedInUser } from '@/store/account/selector';
import { CSM_MEMBERS } from 'src/constants';

interface ReceptionistDetailsStepProps {
	businessName: string;
	websiteUrl: string;
	trialForReseller?: boolean;
	debrand?: boolean;
	handleComplete?: () => void;
	resellerId?: string;
	resellerFeatures?: Features;
	auto_scale?: boolean;
	tempKey: string;
	unreservedReceptionists?: number;
	userId?: string;
	agentType: 'Inbound' | 'Outbound';
	businessType?: string;
	greetingAudio?: string;
	resellerLogoUrl?: string;

	callVolume?: string;
	businessLocations?: string;
	businessRevenue?: string;
	interestedFeatures?: string[];
}

const FINALIZE_REGISTRATION_PATH = '/api/v2/receptionist-dashboard/finalize-registration';
const FINALIZE_RESELLER_REGISTRATION_PATH =
	'/api/v2/receptionist-dashboard/finalize-reseller-registration';

const ReceptionistDetailsStep: React.FC<ReceptionistDetailsStepProps> = ({
	businessName,
	websiteUrl,
	trialForReseller = false,
	debrand = false,
	handleComplete,
	resellerId,
	resellerFeatures,
	auto_scale,
	tempKey,
	userId,
	agentType,
	businessType,
	greetingAudio,
	callVolume,
	businessLocations,
	businessRevenue,
	interestedFeatures,
	resellerLogoUrl,
}) => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const resellerData = useAppSelector(getResellerData);
	const dispatch = useAppDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);

	const [isLoading, setIsLoading] = useState(false);
	const [isPlayingAudio, setIsPlayingAudio] = useState(false);
	const [audioProgress, setAudioProgress] = useState(0);
	const [audioDuration, setAudioDuration] = useState(0);
	const [audioCurrentTime, setAudioCurrentTime] = useState(0);
	const audioRef = React.useRef<HTMLAudioElement | null>(null);

	// Preload audio metadata to get duration
	React.useEffect(() => {
		if (greetingAudio && !audioDuration) {
			const preloadAudioMetadata = async () => {
				try {
					// Convert base64 to binary
					const binaryString = atob(greetingAudio);
					const bytes = new Uint8Array(binaryString.length);
					for (let i = 0; i < binaryString.length; i++) {
						bytes[i] = binaryString.charCodeAt(i);
					}

					// Create blob and audio element
					const audioBlob = new Blob([bytes], { type: 'audio/wav' });
					const audioUrl = URL.createObjectURL(audioBlob);
					const tempAudio = new Audio(audioUrl);

					// Load metadata to get duration
					tempAudio.addEventListener('loadedmetadata', () => {
						setAudioDuration(tempAudio.duration);
						URL.revokeObjectURL(audioUrl);
					});

					tempAudio.load();
				} catch (error) {
					console.error('Error preloading audio metadata:', error);
				}
			};

			preloadAudioMetadata();
		}
	}, [greetingAudio, audioDuration]);

	const createUserMutation = useMutation({
		mutationKey: ['create-user'],
		mutationFn: createUser,
		onError: (error: any) => {
			notification.error({
				message: 'Registration Failed',
				description: error.message || 'Failed to save progress. Please try again.',
			});
		},
	});

	const formatTime = (time: number) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	};

	const playDemoGreeting = async () => {
		if (!greetingAudio || isPlayingAudio) {
			return;
		}

		try {
			setIsPlayingAudio(true);

			// Convert base64 to binary using browser-compatible method
			const binaryString = atob(greetingAudio);
			const bytes = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i);
			}

			// Create blob from bytes
			const audioBlob = new Blob([bytes], { type: 'audio/wav' });
			const audioUrl = URL.createObjectURL(audioBlob);

			const audio = new Audio(audioUrl);
			audioRef.current = audio;

			// Set up event listeners
			audio.addEventListener('loadedmetadata', () => {
				setAudioDuration(audio.duration);
			});

			audio.addEventListener('timeupdate', () => {
				setAudioCurrentTime(audio.currentTime);
				setAudioProgress((audio.currentTime / audio.duration) * 100);
			});

			audio.onended = () => {
				setIsPlayingAudio(false);
				setAudioProgress(0);
				setAudioCurrentTime(0);
				URL.revokeObjectURL(audioUrl);
				audioRef.current = null;
			};

			audio.onerror = () => {
				setIsPlayingAudio(false);
				setAudioProgress(0);
				setAudioCurrentTime(0);
				URL.revokeObjectURL(audioUrl);
				audioRef.current = null;
				notification.error({
					message: 'Audio Error',
					description: 'Failed to play demo greeting. Please try again.',
				});
			};

			await audio.play();
		} catch (error) {
			setIsPlayingAudio(false);
			setAudioProgress(0);
			setAudioCurrentTime(0);
			audioRef.current = null;
			console.error('Error playing demo greeting:', error);
			notification.error({
				message: 'Audio Error',
				description: 'Failed to play demo greeting. Please try again.',
			});
		}
	};

	const stopAudioPlayback = () => {
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}
		setIsPlayingAudio(false);
		setAudioProgress(0);
		setAudioCurrentTime(0);
	};

	const finalizeRegistration = async (values?: {
		username: string;
		email: string;
		password: string;
		name: string;
		phoneNumber?: string;
	}) => {
		setIsLoading(true);

		try {
			let userIdToUse = null;
			let createUserResponse = null;
			if (userId) {
				userIdToUse = userId;
			} else {
				const randomIndex = Math.floor(Math.random() * CSM_MEMBERS.length);
				const selectedCsm = CSM_MEMBERS[randomIndex];

				createUserResponse = await createUserMutation.mutateAsync({
					username: values?.username as string,
					emailAddress: values?.email as string,
					password: values?.password as string,
					type: resellerId ? 'SubAccount' : 'Client',
					trial: Boolean(trialForReseller),
					resellerId: resellerId || undefined,
					subAccountType: resellerId ? 'Inbound' : undefined,
					businessName,
					businessWebsite: websiteUrl,
					businessType,
					callVolume,
					businessLocations,
					businessRevenue,
					interestedFeatures,
					phoneNumber: values?.phoneNumber as string,
					csm: selectedCsm.email,
				});

				if (createUserResponse.success && createUserResponse?.data?.id) {
					userIdToUse = createUserResponse?.data?.id;
				} else {
					return;
				}
			}

			const payload = {
				userId: userIdToUse,
				agentType,
				temp_key: tempKey,
				trialForReseller,
				emailAddress: userId
					? loggedInUser.emailAddress
					: createUserResponse?.data?.emailAddress || values?.email,
				name: values?.name || 'My First AI Agent',
				time_zone: momentTimeZone.tz.guess(),
				...(debrand && {
					test: Boolean(trialForReseller),
					resellerId: resellerData.id || resellerId,
					reseller_client_email: userId ? loggedInUser.emailAddress : values?.email,
					features: resellerData.features || resellerFeatures,
					auto_scale: resellerData.auto_scale_receptionists || auto_scale,
				}),
			};

			const response = await apiClient.request({
				method: 'POST',
				endPoint: debrand ? FINALIZE_RESELLER_REGISTRATION_PATH : FINALIZE_REGISTRATION_PATH,
				body: payload,
			});

			if (response.success) {
				// Store auth token if provided in response
				if (createUserResponse?.token) {
					localStorage.setItem('AUTH_TOKEN', JSON.stringify(createUserResponse?.token));

					// Show success notification
					notification.success({
						message: 'Registration Successful',
						description: 'Your account has been created successfully.',
					});

					if (trialForReseller) {
						handleComplete?.();
						dispatch(
							updateOnboardingInfo({ registerationCompleted: true, password: values?.password }),
						);
						posthog.capture('completed-registration-v5');
						return;
					}

					// Add a small delay to show the success message
					await new Promise((resolve) => setTimeout(resolve, 1000));

					posthog.capture('completed-registration-v5');

					// Navigate to dashboard with registration success origin
					if (debrand) {
						handleComplete?.();
					} else {
						navigate('/?origin=registration-success');
					}
				} else {
					handleComplete?.();
				}
			} else {
				notification.error({
					message: 'Registration Failed',
					description: response.message || 'Failed to complete registration. Please try again.',
				});
			}
		} catch (err: any) {
			console.error('Error finalizing registration:', err);
			notification.error({
				message: 'Registration Error',
				description:
					err.message ||
					'We are having trouble registering you. Please try again or contact support.',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = (values: {
		username: string;
		email: string;
		password: string;
		name: string;
		phoneNumber?: string;
	}) => {
		posthog.capture('submitted-receptionist-details-registration-v5');

		finalizeRegistration(values);
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
			{/* Floating Logo */}
			{(!debrand || (debrand && resellerLogoUrl)) && (
				<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
					<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
						<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-indigo-200 flex items-center justify-center">
							<img
								src={debrand ? resellerLogoUrl : 'new_logo.png'}
								alt=""
								className={`rounded-lg ${debrand ? 'w-12 h-12' : 'w-6 h-6'}`}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Background Decorative Elements */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
				<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
			</div>

			<div className="mt-12 md:mt-0 min-h-screen flex w-full relative z-10">
				{/* Left side - AI Ready Status */}
				<div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 flex-col justify-center relative">
					<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

					<div className="relative z-10 max-w-lg mx-auto p-6 text-center">
						{/* Enhanced AI Orb */}
						<motion.div
							className="mb-8 flex justify-center"
							animate={{ rotate: 360 }}
							transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
						>
							<div className="relative w-32 h-32">
								<motion.div
									className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-300 to-blue-500 opacity-80 shadow-2xl"
									animate={{
										scale: [1, 1.1, 1],
									}}
									transition={{ duration: 3, repeat: Infinity }}
								/>
								<div className="absolute inset-4 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
									<div className="w-4 h-8 bg-white rounded-full opacity-80" />
									<div className="w-4 h-6 bg-white rounded-full opacity-60 ml-1" />
									<div className="w-4 h-4 bg-white rounded-full opacity-40 ml-1" />
									<div className="w-4 h-8 bg-white rounded-full opacity-60 ml-1" />
								</div>
							</div>
						</motion.div>

						{/* Enhanced Title */}
						<h2 className="text-3xl font-bold mb-3 leading-tight text-white">
							<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
								{businessName}
							</span>{' '}
							Receptionist is Ready!
						</h2>
						<p className="text-blue-100 text-base leading-relaxed">
							Your AI receptionist is ready to handle calls for your business
						</p>
					</div>
				</div>

				{/* Right side - Account Creation Form */}
				<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
					<div className="w-full max-w-md">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							className="flex-1 flex flex-col"
						>
							{/* Header */}
							<div className="text-center mb-6">
								<h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
									Create Your Account
								</h1>
								<p className="text-gray-600 text-sm">
									Set up your account to start testing your AI receptionist
								</p>
							</div>

							{/* Enhanced Card */}
							<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
								<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />

								<div className="p-6">
									<Form
										form={form}
										layout="vertical"
										onFinish={handleSubmit}
										requiredMark={false}
										className="space-y-4"
									>
										{userId && (
											<Form.Item
												name="name"
												label={
													<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
														AI Receptionist Name *
													</span>
												}
												rules={[
													{ required: true, message: 'Please give your AI receptionist a name' },
													{ max: 50, message: 'Name must be less than 50 characters' },
												]}
												className="mb-3"
											>
												<Input
													placeholder="What would you like to name your AI receptionist?"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>
										)}

										{!userId && (
											<>
												<Form.Item
													name="phoneNumber"
													label={
														<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
															Personal Contact Number *
														</span>
													}
													rules={[
														{
															required: true,
															message: 'Please enter your personal contact number',
														},
													]}
													className="mb-3"
												>
													<PhoneInput
														country={'us'}
														inputClass="!w-full !h-12 !rounded-xl !border-2 !border-gray-200 hover:!border-green-400 focus:!border-green-500 !text-base !bg-gray-50/50 !transition-all !duration-200"
														containerClass="!w-full"
														buttonClass="!h-12 !rounded-l-xl !border-2 !border-gray-200 hover:!border-green-400 !bg-gray-50/50"
														placeholder="Enter your personal contact number"
													/>
												</Form.Item>

												<Form.Item
													name="username"
													label={
														<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
															Username *
														</span>
													}
													rules={[
														{ required: true, message: 'Please choose a username' },
														{
															pattern: /^[a-zA-Z0-9]+$/,
															message:
																'Username can only contain letters and numbers (no spaces or special characters)',
														},
													]}
													className="mb-3"
												>
													<Input
														placeholder="Enter your username"
														className="h-12 rounded-xl border-2 border-gray-200 hover:border-purple-400 focus:border-purple-500 transition-all duration-200 bg-gray-50/50"
														size="large"
													/>
												</Form.Item>

												<Form.Item
													name="email"
													label={
														<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
															Email *
														</span>
													}
													rules={[
														{ required: true, message: 'Please enter your email' },
														{ type: 'email', message: 'Please enter a valid email' },
													]}
													className="mb-3"
												>
													<Input
														placeholder="Enter your email address"
														className="h-12 rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
														size="large"
													/>
												</Form.Item>

												<Form.Item
													name="password"
													label={
														<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
															Password *
														</span>
													}
													rules={[
														{ required: true, message: 'Please create a password' },
														{ min: 8, message: 'Password must be at least 8 characters' },
													]}
													className="mb-4"
												>
													<Input.Password
														placeholder="Create a secure password"
														className="h-12 rounded-xl border-2 border-gray-200 hover:border-red-400 focus:border-red-500 transition-all duration-200 bg-gray-50/50"
														size="large"
													/>
												</Form.Item>
											</>
										)}

										{/* Audio Preview */}
										{greetingAudio && (
											<div className="bg-blue-50 rounded-md px-3 py-2 my-2">
												<p className="text-sm mb-4 font-medium text-gray-600">
													Hear how your AI receptionist will greet callers
												</p>

												<div className="flex items-center gap-3">
													<Button
														className="h-10 w-10 min-w-10 min-h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 border-0 text-white font-medium rounded-full"
														icon={
															isPlayingAudio ? (
																<Pause className="w-4 h-4" />
															) : (
																<Play className="w-4 h-4" />
															)
														}
														onClick={isPlayingAudio ? stopAudioPlayback : playDemoGreeting}
														loading={isPlayingAudio && audioProgress === 0}
														disabled={!greetingAudio}
													/>

													<div className="space-y-2 w-full">
														<div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
															<div
																className="h-full bg-blue-600 rounded-full transition-all duration-100"
																style={{ width: `${audioProgress}%` }}
															/>
														</div>
														<div className="flex justify-between text-xs">
															<span>{formatTime(audioCurrentTime)}</span>
															<span>{formatTime(audioDuration)}</span>
														</div>
													</div>
												</div>
											</div>
										)}

										{/* Submit Button */}
										<Form.Item className="mb-0 mt-6">
											<Button
												type="primary"
												htmlType="submit"
												className="w-full h-12 text-base font-medium rounded-lg bg-blue-600 hover:bg-blue-700 border-0"
												loading={isLoading}
											>
												Test my Receptionist
											</Button>
										</Form.Item>
									</Form>
								</div>
							</Card>
						</motion.div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReceptionistDetailsStep;
